import React, { useState, useEffect } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import '../rank.css'
import SiteChartModal from './SiteChartModal'
import * as XLSX from 'xlsx'

interface BenchmarkTableProps {
  parameter: {
    name: string
    unit: string
  }
  data?: {
    site_id?: number
    name: string
    feature_value: string
    electricity_consumption?: number
    gas_consumption?: number
    total_combined_consumption?: number
    consumption_per_feature: number
    average_per_feature: number
  }[]
  company: string
  start_date: string
  end_date: string
  meterType: string
}

const BenchmarkTable: React.FC<BenchmarkTableProps> = ({
  parameter,
  data,
  company,
  start_date,
  end_date,
  meterType,
}) => {
  const [sortField, setSortField] = useState<string>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [rankedData, setRankedData] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSite, setSelectedSite] = useState<any>('')
  const [selectedSiteName, setSelectedSiteName] = useState<any>('')
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

  // Meter type'a göre dinamik header'ları belirle
  const getHeaders = () => {
    const baseHeaders = [
      { key: 'checkbox', label: '', sortable: false },
      { key: 'rank', label: 'Rank', sortable: true },
      { key: 'name', label: 'Site', sortable: true },
      { key: 'feature_value', label: `${parameter.name} (${parameter.unit})`, sortable: true },
    ]

    const consumptionHeaders = []

    switch (meterType) {
      case 'electricity':
        consumptionHeaders.push({
          key: 'electricity_consumption',
          label: 'Total Electric Consumption (kWh)',
          sortable: true,
        })
        break
      case 'gas':
        consumptionHeaders.push({
          key: 'gas_consumption',
          label: 'Total Gas Consumption (kWh)',
          sortable: true,
        })
        break
      case 'combine':
        consumptionHeaders.push(
          {
            key: 'electricity_consumption',
            label: 'Total Electric Consumption (kWh)',
            sortable: true,
          },
          {
            key: 'gas_consumption',
            label: 'Total Gas Consumption (kWh)',
            sortable: true,
          },
          {
            key: 'total_combined_consumption',
            label: 'Total Combined Consumption (kWh)',
            sortable: true,
          }
        )
        break
    }

    const endHeaders = [
      {
        key: 'average_per_feature',
        label: `Average Consumption Per ${parameter.unit} (kWh)`,
        sortable: true,
      },
      {
        key: 'consumption_per_feature',
        label: `Consumption Per ${parameter.unit} (kWh)`,
        sortable: true,
      },
    ]

    return [...baseHeaders, ...consumptionHeaders, ...endHeaders]
  }

  const headers = getHeaders()

  const handleSort = (field: string) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortDirection(direction)
  }

  const rankData = (sortedData: any[]) => {
    const ranked = sortedData.map((item, index) => ({
      ...item,
      rank: index + 1,
    }))
    setRankedData(ranked)
  }

  useEffect(() => {
    if (data) {
      const sortedData = data
        ? [...data]
            .sort((a, b) => {
              const aValue = parseFloat(a.consumption_per_feature.toString())
              const bValue = parseFloat(b.consumption_per_feature.toString())
              return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
            })
            .map((item, index) => ({ ...item, rank: index + 1 }))
        : []

      rankData(sortedData)
    }
  }, [data])

  const sortedData = rankedData
    ? [...rankedData].sort((a, b) => {
        const aValue = a[sortField as keyof typeof a]
        const bValue = b[sortField as keyof typeof b]

        if (typeof aValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        } else {
          return sortDirection === 'asc'
            ? parseFloat(aValue) - parseFloat(bValue)
            : parseFloat(bValue) - parseFloat(aValue)
        }
      })
    : []

  const renderSortIcon = (field: string) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? (
        <KTSVG
          path='/media/icons/duotune/arrows/arr066.svg'
          className='svg-icon svg-icon-1 svg-icon-primary ms-2'
        />
      ) : (
        <KTSVG
          path='/media/icons/duotune/arrows/arr065.svg'
          className='svg-icon svg-icon-1 svg-icon-primary ms-2'
        />
      )
    }
    return (
      <KTSVG
        path='/media/icons/duotune/arrows/arr065.svg'
        className='svg-icon svg-icon-1 svg-icon-gray-500 ms-2'
      />
    )
  }

  const openModal = (siteData: any) => {
    setSelectedSite(siteData.site_id ?? '')
    setSelectedSiteName(siteData.name ?? '')
  }

  useEffect(() => {
    if (selectedSite) {
      setIsModalOpen(true)
    }
  }, [selectedSite])

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedSite(null)
  }

  const exportToExcel = () => {
    if (!data || data.length === 0) return

    const dataToExport =
      selectedRows.size > 0 ? sortedData.filter((item) => selectedRows.has(item.rank)) : sortedData

    const excelData = dataToExport.map((item) => {
      const row: Record<string, string | number> = {
        Rank: item.rank,
        Site: item.name,
        [`${parameter.name} (${parameter.unit})`]: parseFloat(item.feature_value).toFixed(2),
      }

      // Meter type'a göre consumption kolonlarını ekle
      if (meterType === 'electricity' || meterType === 'combine') {
        row['Total Electric Consumption (kWh)'] = parseFloat(
          item.electricity_consumption || 0
        ).toFixed(2)
      }
      if (meterType === 'gas' || meterType === 'combine') {
        row['Total Gas Consumption (kWh)'] = parseFloat(item.gas_consumption || 0).toFixed(2)
      }
      if (meterType === 'combine') {
        row['Total Combined Consumption (kWh)'] = parseFloat(
          item.total_combined_consumption || 0
        ).toFixed(2)
      }

      row[`Average Consumption Per ${parameter.unit} (kWh)`] = parseFloat(
        item.average_per_feature.toString()
      ).toFixed(2)
      row[`Consumption Per ${parameter.unit} (kWh)`] = parseFloat(
        item.consumption_per_feature.toString()
      ).toFixed(2)

      return row
    })

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)
    XLSX.utils.book_append_sheet(wb, ws, 'Benchmark Data')
    XLSX.writeFile(wb, `benchmark_data_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const handleRowSelect = (rank: number) => {
    setSelectedRows((prev) => {
      const newSelection = new Set(prev)
      if (newSelection.has(rank)) {
        newSelection.delete(rank)
      } else {
        newSelection.add(rank)
      }
      return newSelection
    })
  }

  const handleSelectAll = () => {
    if (selectedRows.size === sortedData.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(sortedData.map((item) => item.rank)))
    }
  }

  const handleClearSelection = () => {
    setSelectedRows(new Set())
  }

  // Hücre değerini render et
  const renderCellValue = (item: any, key: string) => {
    const value = item[key]
    if (value === undefined || value === null) return '-'

    if (typeof value === 'number' || !isNaN(parseFloat(value))) {
      return parseFloat(value).toFixed(2)
    }
    return value
  }

  return (
    <div className={`card mb-5 mb-xl-8`}>
      <div className='card-header border-0 pt-5'>
        <div className='card-title'>
          <div className='d-flex align-items-center position-relative my-1'>
            <button className='btn btn-sm btn-primary me-2' onClick={exportToExcel}>
              <i className='fas fa-file-excel me-2'></i>
              Export to Excel
            </button>
            <div className='d-flex align-items-center'>
              {selectedRows.size > 0 ? (
                <>
                  <button className='btn btn-sm btn-danger me-2' onClick={handleClearSelection}>
                    <i className='fas fa-times me-2'></i>
                    Clear Selection
                  </button>
                  <span className='text-muted me-2'>
                    {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected
                  </span>
                </>
              ) : (
                <button className='btn btn-sm btn-primary' onClick={handleSelectAll}>
                  <i className='fas fa-check-double me-2'></i>
                  Select All
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          {data && data.length > 0 ? (
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bold text-muted'>
                  {headers.map((header) => (
                    <th
                      key={header.key}
                      className={header.key === 'checkbox' ? 'min-w-50px' : 'min-w-120px'}
                      onClick={header.sortable ? () => handleSort(header.key) : undefined}
                      style={header.sortable ? { cursor: 'pointer' } : {}}
                    >
                      {header.key === 'checkbox' ? (
                        <input
                          type='checkbox'
                          className='form-check-input form-check-input-solid'
                          checked={selectedRows.size === sortedData.length}
                          onChange={handleSelectAll}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                      ) : (
                        <>
                          {header.label}
                          {header.sortable && renderSortIcon(header.key)}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => (
                  <tr
                    key={index}
                    className={`${item.rank <= 3 ? `rank-${item.rank}` : ''} ${
                      selectedRows.has(item.rank) ? 'table-active' : ''
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRowSelect(item.rank)}
                  >
                    {headers.map((header) => (
                      <td key={header.key}>
                        {header.key === 'checkbox' ? (
                          <input
                            type='checkbox'
                            className='form-check-input form-check-input-solid'
                            checked={selectedRows.has(item.rank)}
                            onChange={(e) => {
                              e.stopPropagation()
                              handleRowSelect(item.rank)
                            }}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                        ) : header.key === 'rank' ? (
                          <span style={{ fontWeight: 'bold', textAlign: 'center' }}>
                            {item.rank}
                          </span>
                        ) : header.key === 'name' ? (
                          <a
                            href='#'
                            onClick={(e) => {
                              e.stopPropagation()
                              openModal(item)
                            }}
                            className='text-dark fw-bold text-hover-primary fs-6'
                          >
                            {item.name}
                          </a>
                        ) : (
                          <span className='text-dark fw-bold fs-6'>
                            {renderCellValue(item, header.key)}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className='text-muted'>No data available.</p>
          )}
        </div>
      </div>

      {isModalOpen && (
        <SiteChartModal
          benchmarkData={{
            siteName: selectedSiteName,
            companyValue: company,
            siteValue: selectedSite,
            start_date: start_date,
            end_date: end_date,
          }}
          show={true}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default BenchmarkTable
